import { PositiveNegativeAvgDatapoint } from 'src/Esg/Dmav2/types';
import _ from 'lodash';
import { ChartOptions, TooltipItem } from 'chart.js';
import { t } from 'i18next';

export const getDataPoint = (
  summary: PositiveNegativeAvgDatapoint,
  adjustedSummary: PositiveNegativeAvgDatapoint,
  label: string,
  description: string,
  color?: string,
  opacity: number = 1
) => {
  // Convert hex color to rgba if opacity is less than 1
  let backgroundColor = color || '#D9D9D9';
  if (opacity < 1 && backgroundColor.startsWith('#')) {
    // Convert hex to rgba
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);
    backgroundColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return {
    label,
    data: [
      {
        x: adjustedSummary.negative_avg!,
        y: adjustedSummary.positive_avg!,
        description,
        realNegativeScore: summary.negative_avg,
        realPositiveScore: summary.positive_avg,
      },
    ],
    backgroundColor,
    pointRadius: 20,
    pointHoverRadius: 25,
    borderColor: backgroundColor,
    borderWidth: 1,
  };
};

// Helper function to calculate Euclidean distance between two points
function calculateDistance(
  p1: { positive_avg: number | null; negative_avg: number | null },
  p2: { positive_avg: number | null; negative_avg: number | null }
): number {
  const x1 = p1.negative_avg ?? 0;
  const y1 = p1.positive_avg ?? 0;
  const x2 = p2.negative_avg ?? 0;
  const y2 = p2.positive_avg ?? 0;

  return Math.sqrt((x2 - x1)**2 + (y2 - y1)**2);
}

// Implementation of a simplified DBSCAN clustering algorithm
function clusterPoints<T extends PositiveNegativeAvgDatapoint>(
  points: T[],
  epsilon: number = 0.15, // Maximum distance between points in a cluster (reduced for more precise clusters)
  minPoints: number = 2 // Minimum points to form a cluster (including the point itself)
): { clusters: T[][]; noise: T[] } {
  const visited: boolean[] = Array(points.length).fill(false);
  const clustered: boolean[] = Array(points.length).fill(false);
  const clusters: T[][] = [];
  const noise: T[] = [];

  // Find neighbors within epsilon distance
  function getNeighbors(pointIndex: number): number[] {
    const neighbors: number[] = [];
    const point = points[pointIndex];

    for (let i = 0; i < points.length; i++) {
      if (i !== pointIndex && calculateDistance(point, points[i]) <= epsilon) {
        neighbors.push(i);
      }
    }

    return neighbors;
  }

  // Process each point for clustering
  for (let i = 0; i < points.length; i++) {
    if (visited[i]) continue;

    visited[i] = true;
    const neighbors = getNeighbors(i);

    // Not enough points to form a cluster
    if (neighbors.length + 1 < minPoints) {
      noise.push(points[i]);
      continue;
    }

    // Start a new cluster
    const currentCluster: T[] = [points[i]];
    clustered[i] = true;

    // Process neighbors
    let neighborIndex = 0;
    while (neighborIndex < neighbors.length) {
      const current = neighbors[neighborIndex];

      // If not visited, mark as visited and find its neighbors
      if (!visited[current]) {
        visited[current] = true;
        const newNeighbors = getNeighbors(current);

        // If enough neighbors, add them to the processing queue
        if (newNeighbors.length + 1 >= minPoints) {
          neighbors.push(
            ...newNeighbors.filter((n) => !neighbors.includes(n) && n !== i)
          );
        }
      }

      // Add to cluster if not already in a cluster
      if (!clustered[current]) {
        currentCluster.push(points[current]);
        clustered[current] = true;
      }

      neighborIndex++;
    }

    clusters.push(currentCluster);
  }

  return { clusters, noise };
}

export function adjustDataPoints<T extends PositiveNegativeAvgDatapoint>(
  summaries: T[]
): T[] {
  // Create a deep copy of the summaries array
  const adjustedSummaries = _.cloneDeep(summaries);

  // Handle null values and ensure all points have numeric coordinates
  adjustedSummaries.forEach((summary) => {
    if (!summary?.positive_avg) {
      summary.positive_avg = 0;
    }
    if (!summary?.negative_avg) {
      summary.negative_avg = 0;
    }
  });

  // Use density-based clustering to find clusters
  const { clusters } = clusterPoints(adjustedSummaries);

  // Process each cluster
  clusters.forEach((cluster) => {
    if (cluster.length > 1) {
      // Calculate cluster center (centroid)
      const centroidX =
        cluster.reduce((sum, p) => sum + (p.negative_avg ?? 0), 0) /
        cluster.length;
      const centroidY =
        cluster.reduce((sum, p) => sum + (p.positive_avg ?? 0), 0) /
        cluster.length;

      // Arrange points in a circle around the centroid or in a grid pattern
      cluster.forEach((point, index) => {
        // Leave the first point at its original position if close enough to centroid
        if (
          index === 0 &&
          calculateDistance(point, {
            negative_avg: centroidX,
            positive_avg: centroidY,
          }) < 0.05
        ) {
          return;
        }

        // Calculate angle for circular arrangement (distributes points evenly in a circle)
        const angle = (index / cluster.length) * 2 * Math.PI;

        // Radius based on cluster size - larger clusters need more space
        // The sqrt creates a more pleasing distribution as cluster size grows
        const radius =
          0.05 * Math.sqrt(cluster.length) * (1 + 0.2 * (index % 3)); // Vary radius slightly to avoid perfect circles

        // Position the point around the centroid
        point.negative_avg = centroidX + radius * Math.cos(angle);
        point.positive_avg = centroidY + radius * Math.sin(angle);
      });
    }
  });

  // Ensure all points are within the valid range
  for (const item of adjustedSummaries) {
    item.positive_avg = Math.min(3, Math.max(0, item.positive_avg!));
    item.negative_avg = Math.min(3, Math.max(0, item.negative_avg!));
  }

  return adjustedSummaries;
}

export const scatterOptions: ChartOptions<'scatter'> = {
  maintainAspectRatio: true,
  aspectRatio: 1,
  scales: {
    x: {
      type: 'linear',
      min: 0,
      max: 3.5,
      ticks: {
        stepSize: 0.5,
        callback(value) {
          return Number(value) > 3 ? '' : value;
        },
        padding: 10,
      },
      grid: {
        display: true,
        drawOnChartArea: true,
        lineWidth: 1,
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },
    y: {
      min: 0,
      max: 3.5,
      ticks: {
        stepSize: 0.5,
        callback(value) {
          return Number(value) > 3 ? '' : value;
        },
        padding: 10,
      },
      grid: {
        display: true,
        drawOnChartArea: true,
        lineWidth: 1,
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(context: TooltipItem<'scatter'>) {
          const { description, realNegativeScore, realPositiveScore } =
            context.raw as {
              description: string;
              realNegativeScore: number;
              realPositiveScore: number;
            };
          return [
            description,
            `${t(
              'esg:dma.stakeholderAnalytics.dashboard.scatterPlot.negativeScore'
            )}: ${realNegativeScore?.toFixed(2) || 0}, ${t(
              'esg:dma.stakeholderAnalytics.dashboard.scatterPlot.positiveScore'
            )}: ${realPositiveScore?.toFixed(2) || 0}`,
          ];
        },
      },
    },
  },
};
